<script setup lang="ts">
import { ChevronDownIcon } from '@lucide/vue'
import { reactiveOmit } from '@vueuse/core'
import type { CollapsibleTriggerProps } from 'reka-ui'
import { CollapsibleTrigger, useForwardProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { cn } from '../../../lib/utils'

/**
 * The header of a `Collapsible`: a native `type="button"` (so Enter and Space open it and it never submits the
 * form it sits in) that says whether it is open through `aria-expanded` and `data-state`. It speaks the brand
 * control voice (mono at the system tracking, uppercase) and carries a chevron that turns when open. Whatever
 * the screen puts in the slot is the header's title; `count`, when given, is drawn beside it on the `--input`
 * hairline, so a screen can say how much the section holds without styling a chip of its own. No outline
 * suppression: the `@layer base` `:focus-visible` ring shows (BG §22.3).
 *
 * With `as-child` the trigger draws nothing of its own: the slot is the element that opens and closes the
 * `Collapsible` (a sidebar menu button, for example) and keeps its own look, so the brand classes, the title
 * wrapper, the count and the chevron are all left out and only a `class` the caller passes is applied.
 */
const props = defineProps<CollapsibleTriggerProps & { class?: HTMLAttributes['class']; count?: number }>()

const delegatedProps = reactiveOmit(props, 'class', 'count')
const forwardedProps = useForwardProps(delegatedProps)
</script>

<template>
  <CollapsibleTrigger
    data-slot="collapsible-trigger"
    v-bind="forwardedProps"
    :class="
      props.asChild
        ? props.class
        : cn(
            'text-foreground hover:text-primary focus-visible:ring-ring/50 flex w-full items-center justify-between gap-2 py-2 font-mono text-sm tracking-[var(--tracking-system)] uppercase transition-colors focus-visible:ring-3 disabled:cursor-not-allowed disabled:opacity-50 [&[data-state=open]>svg]:rotate-180 [&_svg]:pointer-events-none [&_svg]:shrink-0',
            props.class,
          )
    "
  >
    <slot v-if="props.asChild" />
    <template v-else>
      <span class="inline-flex items-center gap-2"><span data-slot="collapsible-title"><slot /></span> <span v-if="count !== undefined" data-slot="collapsible-count" class="border-input text-muted-foreground inline-flex min-w-6 items-center justify-center border px-1.5 text-xs">{{ count }}</span></span>
      <ChevronDownIcon class="text-muted-foreground size-4 transition-transform" />
    </template>
  </CollapsibleTrigger>
</template>
